import React, { useState, useRef, useEffect } from "react";
import { Save, Users, Plus, Search, ChevronLeft, FileText, FileSignature, Package, CreditCard, ClipboardList, Award, History as HistoryIcon } from "lucide-react";

import ExistingConnectionsSelector from "./ExistingComponent";
import CustomerDetails from "./CustomerDetails";
import SystemConfig from "./SalesSystemConfig";
import ExistingSystemConfig from "./ExistingSystemConfig";
import TelecomQuotation from "./TelecomQuotation";
import FibreContractForm from "./FibreContractForm";
import RentalDocumentForm from "./RentalDocumentForm";
import AssignmentLetterTab from "./AssignmentLetterTab";
import RentalAgreementTab from "./RentalAgreementTab";  // ← KEEP
import CoaTab from "./CoaTab";  // ← KEEP
import { useSearchParams } from "react-router-dom";
import { mergeNonEmpty } from "../utils/mergeNonEmpty";
import LogsTab from "./LogsTab";

// ─── Admin check helper ──────────────────────────────────────────────────────
// Matches the same localStorage key/value used in ExistingComponent.jsx:
//   const userRole = localStorage.getItem('Role');
//   const isAdmin = userRole === 'Admin';
// NOTE: isAdmin is retained for other potential admin-only behavior, but it no
// longer gates the Assignment Letter / Rental Agreement / Acceptance
// Certificate (COA) tabs — those are now visible to all users.
const getIsAdmin = () => {
  try {
    return localStorage.getItem("Role") === "Admin";
  } catch (err) {
    console.error("Failed to determine admin status:", err);
    return false;
  }
};
const getCurrentUser = () => {
  try {
    return { role: localStorage.getItem("Role") };
  } catch (err) {
    console.error("Failed to determine current user:", err);
    return { role: null };
  }
};

// Tabs that should only be visible/reachable by admins
const ADMIN_ONLY_TAB_IDS = [];


// ─── GoCardless Tab Component ───────────────────────────────────────────────
const GoCardlessTab = ({ connectionData, selectedConnectionId }) => {
  const [isSending, setIsSending] = useState(false);
  const [sendStatus, setSendStatus] = useState(null);
  const [managerEmail, setManagerEmail] = useState(connectionData?.director_email || "");
  const [managerName, setManagerName] = useState(connectionData?.md_name || "");
  const [customMessage, setCustomMessage] = useState("");

  useEffect(() => {
    if (connectionData?.director_email) setManagerEmail(connectionData.director_email);
    if (connectionData?.md_name) setManagerName(connectionData.md_name);
  }, [connectionData]);

  const handleSendEmail = async () => {
    if (!managerEmail) {
      setSendStatus({ type: "error", message: "Please enter a manager email address." });
      return;
    }
    setIsSending(true);
    setSendStatus(null);
    try {
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
      const payload = {
        connection_id: selectedConnectionId,
        manager_email: managerEmail,
        manager_name: managerName,
        custom_message: customMessage,
        company_name: connectionData?.company_name,
        customer_name: connectionData?.md_name,
      };
      const response = await fetch(`${API_BASE_URL}/gocardless/send-link`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
        body: JSON.stringify(payload),
      });
      if (response.ok) {
        setSendStatus({ type: "success", message: `GoCardless payment link successfully sent to ${managerEmail}.` });
      } else {
        const err = await response.json().catch(() => ({}));
        setSendStatus({ type: "error", message: err?.message || "Failed to send email. Please try again." });
      }
    } catch (error) {
      console.error("GoCardless email error:", error);
      setSendStatus({ type: "error", message: "Network error. Please check your connection and try again." });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="mt-4 space-y-6">
      {connectionData && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center gap-3">
            <div className="bg-blue-100 w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0">
              <CreditCard className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h4 className="font-semibold text-blue-900">Direct Debit Setup</h4>
              <p className="text-blue-700 text-sm">{connectionData.company_name} — {connectionData.md_name}</p>
              {connectionData.director_email && <p className="text-xs text-blue-500">{connectionData.director_email}</p>}
            </div>
          </div>
        </div>
      )}
      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm space-y-5">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Send GoCardless Payment Link</h3>
          <p className="text-sm text-gray-500 mt-1">Enter the manager's details below. An email with the GoCardless direct debit authorisation link will be sent to them immediately.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Manager Email Address <span className="text-red-500">*</span></label>
            <input type="email" value={managerEmail} onChange={(e) => setManagerEmail(e.target.value)} placeholder="manager@company.com" className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Manager Name <span className="text-gray-400 font-normal">(optional)</span></label>
            <input type="text" value={managerName} onChange={(e) => setManagerName(e.target.value)} placeholder="e.g. John Smith" className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
          </div>
        </div>
        {sendStatus && (
          <div className={`flex items-start gap-3 p-4 rounded-lg border text-sm ${sendStatus.type === "success" ? "bg-green-50 border-green-200 text-green-800" : "bg-red-50 border-red-200 text-red-800"}`}>
            <span className="text-lg leading-none flex-shrink-0">{sendStatus.type === "success" ? "✓" : "✕"}</span>
            <p>{sendStatus.message}</p>
          </div>
        )}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">What the email will include</p>
          <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
            <li>GoCardless direct debit authorisation link</li>
            <li>Customer & company details ({connectionData?.company_name || "—"})</li>
          </ul>
        </div>
        <div className="flex justify-end pt-2">
          <button onClick={handleSendEmail} disabled={isSending || !managerEmail}
            className={`inline-flex items-center gap-2 px-6 py-2.5 rounded-lg text-sm font-medium text-white transition-colors ${isSending || !managerEmail ? "bg-gray-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700 active:bg-blue-800"}`}>
            <CreditCard className="h-4 w-4" />
            {isSending ? "Sending..." : "Send GoCardless Link"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Tab Button helper ────────────────────────────────────────────────────────
const TabBtn = ({ id, activeTab, setActiveTab, disabled, locked, icon: Icon, label }) => (
   <button
    onClick={() => !disabled && setActiveTab(id)}
    disabled={disabled}
    title={locked ? "Click Update to save System Config first" : undefined}
    className={`px-4 py-2 text-xl font-medium rounded-md transition-all duration-300 ${
      activeTab === id
        ? "bg-white text-blue-900"
        : disabled
        ? "text-gray-400 cursor-not-allowed"
        : locked
        ? "text-white/40 cursor-not-allowed"
        : "text-white hover:from-gray-700 hover:via-gray-800 hover:to-gray-900"
    }`}
  >
    {Icon && <Icon className="inline w-5 h-5 mr-2" />}
    {label}
  </button>
);

// ─── Shared tab definitions ───────────────────────────────────────────────────
const ALL_EXISTING_TABS = [
  { id: "system",           label: "SYSTEM CONFIG"      },
  { id: "quote",            label: "QUOTE",              icon: FileText      },
  { id: "contract",         label: "CONTRACT",           icon: FileSignature },
  { id: "rental",           label: "RENTAL",             icon: Package       },
  { id: "gocardless",       label: "GOCARDLESS",         icon: CreditCard    },
  { id: "assignment",       label: "ASSIGNMENT LETTER",  icon: ClipboardList },
  { id: "rental-agreement", label: "RENTAL AGREEMENT",   icon: FileText      },
  { id: "coa",              label: "ACCEPTANCE CERTIFICATE", icon: Award     },
  { id: "logs", label: "LOGS", icon: HistoryIcon },
];

// ─── Main Component ───────────────────────────────────────────────────────────
const NewQuoteForm = ({ onSave, onCancel,currentUser  }) => {
  const [form] = useState({});
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState("connection-type");
  const [connectionType, setConnectionType] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [savedConnectionId, setSavedConnectionId] = useState(null);
  const [isSavingConnection, setIsSavingConnection] = useState(false);
  const [connectionData, setConnectionData] = useState(null);
  const [systemData, setSystemData] = useState(null);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [showExistingConnections, setShowExistingConnections] = useState(false);
  const [showExistingConfig, setShowExistingConfig] = useState(false);
  const [showExistingQuote, setShowExistingQuote] = useState(false);
  const customerDetailsRef = useRef();
  const systemConfigRef = useRef();
  const contractFormRef = useRef(null);
  const [isConfigDirty, setIsConfigDirty] = useState(false);

const UNSAVED_MSG =
  "You have unsaved changes in System Config.\n\nPlease click Update before moving to another tab.";

const canLeave = () => {
  if (isConfigDirty) {
    alert(UNSAVED_MSG);
    return false;
  }
  return true;
};

const goToTab = (id) => {
  if (id === activeTab) return;
  if (!canLeave()) return;
  setActiveTab(id);
};

  // Determine admin status once on mount (kept for any future admin-only
  // features; no longer used to gate assignment / rental-agreement / coa)
  const [isAdmin] = useState(() => getIsAdmin());
 
 console.log("currentUser",currentUser);
  // Tabs filtered for the currently logged-in user
  const EXISTING_TABS = isAdmin
    ? ALL_EXISTING_TABS
    : ALL_EXISTING_TABS.filter((tab) => !ADMIN_ONLY_TAB_IDS.includes(tab.id));

  const handleConnectionSaved = (connectionId, data) => {
    setSavedConnectionId(connectionId);
    setConnectionData(data);
    setActiveTab("system");
    setIsSavingConnection(false);
  };

  const handleSystemConfigSaved = (systemConfigData) => {
    setSystemData(systemConfigData);
    setActiveTab("quote");
  };
  const handleConnectionUpdated = (updatedConnection) => {
    setConnectionData((prev) => mergeNonEmpty(prev, updatedConnection));
  };

  useEffect(() => {
    const connectionIdFromUrl = searchParams.get("connectionId");
    const opportunityIdFromUrl = searchParams.get("opportunityId");
    const stepFromUrl = searchParams.get("step");

    if (connectionIdFromUrl && opportunityIdFromUrl) {
      setSavedConnectionId(connectionIdFromUrl);
      const fetchConnection = async () => {
        try {
          const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
          const response = await fetch(`${API_BASE_URL}/connection-details/${connectionIdFromUrl}`, {
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
              Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            },
          });
          if (response.ok) {
            const result = await response.json();
            const connData = result.data ?? result;
            setConnectionData(connData);
            setConnectionType("existing");
            setShowExistingConnections(false);
            setShowExistingConfig(true);

            const stepToTabMap = {
              system_config:    "system",
              quote:            "quote",
              contract:         "contract",
              rental:           "rental",
              gocardless:       "gocardless",
              assignment:       "assignment",
              rental_agreement: "rental-agreement",
              coa:              "coa",
            };
            const targetTab = stepToTabMap[stepFromUrl] || "system";
            setActiveTab(targetTab);
          }
        } catch (err) {
          console.error("Failed to fetch connection for URL param:", err);
        }
      };
      fetchConnection();
    }
  }, [searchParams, isAdmin]);

  const handleExistingConnectionSelect = (connection, stepName) => {
    setSavedConnectionId(connection.id);
    setConnectionData(connection);
    setShowExistingConnections(false);
    setShowExistingConfig(true);

    const stepToTabMap = {
      system_config:    "system",
      quote:            "quote",
      contract:         "contract",
      rental:           "rental",
      gocardless:       "gocardless",
      assignment:       "assignment",
      rental_agreement: "rental-agreement",
      coa:              "coa",
    };
    const targetTab = stepToTabMap[stepName] || "system";
    setActiveTab(targetTab);
  };

  const handleBackFromConfig = () => {
     if (!canLeave()) return;   
    setShowExistingConfig(false);
    setShowExistingQuote(false);
    setShowExistingConnections(true);
  };

  const handleBackFromQuote    = () => setActiveTab("system");
  const handleBackFromContract = () => setActiveTab("quote");
  const handleBackFromRental   = () => setActiveTab("contract");

  const onFinish = async (values) => {
    setIsSubmitting(true);
    try {
      const quoteData = { connection: connectionData, system: systemData, ...values };
      alert("Quote created successfully!");
      onSave(quoteData);
    } catch (error) {
      alert("Please complete all required fields");
      console.error("Validation failed:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // nextTab / prevTab now walk through the full flow, including
  // assignment → rental-agreement → coa, for every user.
  const nextTab = async () => {
    if (activeTab === "connection-type") {
      if (connectionType === "new") setActiveTab("customer");
      else if (connectionType === "existing") setShowExistingConnections(true);
    } else if (activeTab === "customer") {
      setIsSavingConnection(true);
      try {
        if (customerDetailsRef.current) await customerDetailsRef.current.handleSaveConnection();
      } catch (error) {
        console.error("Failed to save connection:", error);
        setIsSavingConnection(false);
      }
    } else if (activeTab === "system")          { if (systemConfigRef.current) await systemConfigRef.current.handleSave(); }
    else if (activeTab === "quote")             setActiveTab("contract");
    else if (activeTab === "contract") {
      await contractFormRef.current?.flushContractNotes();
      setActiveTab("rental");
    }
    else if (activeTab === "rental")            setActiveTab("gocardless");
    else if (activeTab === "gocardless")        setActiveTab("assignment");
    else if (activeTab === "assignment")        setActiveTab("rental-agreement");
    else if (activeTab === "rental-agreement")  setActiveTab("coa");
  };

  const prevTab = () => {
    if      (activeTab === "coa")              setActiveTab("rental-agreement");
    else if (activeTab === "rental-agreement") setActiveTab("assignment");
    else if (activeTab === "assignment")       setActiveTab("gocardless");
    else if (activeTab === "gocardless")       setActiveTab("rental");
    else if (activeTab === "rental")           setActiveTab("contract");
    else if (activeTab === "contract")         setActiveTab("quote");
    else if (activeTab === "quote")            setActiveTab("system");
    else if (activeTab === "system") {
      if (connectionType === "existing") setActiveTab("connection-type");
      else setActiveTab("customer");
    } else if (activeTab === "customer")       setActiveTab("connection-type");
  };

  const resetForm = () => {
    setActiveTab("connection-type");
    setConnectionType("");
    setSavedConnectionId(null);
    setConnectionData(null);
    setSystemData(null);
    setShowExistingConfig(false);
    setShowExistingQuote(false);
  };

  // ── Existing-connection view ──────────────────────────────────────────────
  if (showExistingConnections) {
    return (
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <ExistingConnectionsSelector
          onConnectionSelect={handleExistingConnectionSelect}
          onClose={() => setShowExistingConnections(false)}
        />
        <div className="p-6 border-t border-gray-200">
          <div className="flex justify-between">
            <button onClick={() => setShowExistingConnections(false)}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50">
              Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (showExistingConfig) {
    const existingBackLabel = {
      "coa": "Back to Rental Agreement",
      "rental-agreement": "Back to Assignment Letter",
      assignment:         "Back to GoCardless",
      gocardless:         "Back to Rental",
      rental:             "Back to Contract",
      contract:           "Back to Quote",
      quote:              "Back to System Config",
      system:             "Back to Connections",
    };

    return (
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="p-6">
          <button onClick={handleBackFromConfig} className="flex items-center text-blue-600 hover:text-blue-800 mb-6">
            <ChevronLeft className="h-5 w-5 mr-1" />
            Back to connection selection
          </button>

          {/* Tab Navigation */}
          <div className="flex flex-wrap gap-1 bg-gradient-to-r from-blue-800 via-blue-900 to-black rounded-t-lg p-1 mb-6">
            {EXISTING_TABS.map(({ id, label, icon }) => (
  <TabBtn
    key={id}
    id={id}
    activeTab={activeTab}
    setActiveTab={goToTab}                              // ← was setActiveTab
    locked={isConfigDirty && id !== activeTab}          // ← add (optional, visual)
    icon={icon}
    label={label}
  />
))}
          </div>

          {/* Tab Content */}
          <div>
           {activeTab === "system" && (
  <ExistingSystemConfig
    connection={connectionData}
    selectedConnectionId={savedConnectionId}
    onBack={() => setActiveTab("quote")}       // ← leave as raw setActiveTab
    onSystemConfigSaved={handleSystemConfigSaved}
    onConnectionUpdated={handleConnectionUpdated}
    onDirtyChange={setIsConfigDirty}           // ← add
  />
)}
            {activeTab === "quote" && (
              <TelecomQuotation
                systemData={systemData}
                selectedConnectionId={savedConnectionId}
                connectionData={connectionData}
                onSave={onFinish}
                onBack={() => setActiveTab("system")}
                onNext={() => setActiveTab("contract")}
              />
            )}
            {activeTab === "contract" && (
              <FibreContractForm
                ref={contractFormRef}
                systemData={systemData}
                selectedConnectionId={savedConnectionId}
                connectionData={connectionData}
                onSave={onFinish}
                onBack={() => setActiveTab("quote")}
                onNext={() => setActiveTab("rental")}
              />
            )}
            {activeTab === "rental" && (
              <RentalDocumentForm
                systemData={systemData}
                selectedConnectionId={savedConnectionId}
                connectionData={connectionData}
                onSave={onFinish}
                onBack={() => setActiveTab("contract")}
              />
            )}
            {activeTab === "gocardless" && (
              <GoCardlessTab connectionData={connectionData} selectedConnectionId={savedConnectionId} />
            )}
            {activeTab === "assignment" && (
              <AssignmentLetterTab connectionData={connectionData} selectedConnectionId={savedConnectionId} />
            )}
            {activeTab === "rental-agreement" && (
              <RentalAgreementTab connectionData={connectionData} selectedConnectionId={savedConnectionId} currentUser={currentUser} />
            )}
            {activeTab === "coa" && (
              <CoaTab connectionData={connectionData} selectedConnectionId={savedConnectionId} />
            )}
            {activeTab === "logs" && (
              <LogsTab selectedConnectionId={savedConnectionId} />
            )}
          </div>

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-6 pt-6 border-t border-gray-200">
            <button
              onClick={() => {
                if      (activeTab === "logs")             setActiveTab("coa");
                else if (activeTab === "coa")              setActiveTab("rental-agreement");
                else if (activeTab === "rental-agreement") setActiveTab("assignment");
                else if (activeTab === "assignment")       setActiveTab("gocardless");
                else if (activeTab === "gocardless")       setActiveTab("rental");
                else if (activeTab === "rental")           setActiveTab("contract");
                else if (activeTab === "contract")         setActiveTab("quote");
                else if (activeTab === "quote")            setActiveTab("system");
                else handleBackFromConfig();
              }}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              {existingBackLabel[activeTab] || "Back"}
            </button>

            {activeTab === "quote" && (
              <button onClick={() => setActiveTab("contract")} className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700">
                Continue to Contract
              </button>
            )}
            {activeTab === "contract" && (
              <button onClick={() => setActiveTab("rental")} className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700">
                Continue to Rental
              </button>
            )}
            {activeTab === "rental" && (
              <button onClick={() => setActiveTab("gocardless")} className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700">
                Continue to GoCardless
              </button>
            )}
            {activeTab === "gocardless" && (
              <button onClick={() => setActiveTab("assignment")} className="px-4 py-2 bg-purple-600 text-white rounded-md text-sm font-medium hover:bg-purple-700">
                Continue to Assignment Letter
              </button>
            )}
            {activeTab === "assignment" && (
              <button onClick={() => setActiveTab("rental-agreement")} className="px-4 py-2 bg-purple-600 text-white rounded-md text-sm font-medium hover:bg-purple-700">
                Continue to Rental Agreement
              </button>
            )}
            {activeTab === "rental-agreement" && (
              <button onClick={() => setActiveTab("coa")} className="px-4 py-2 bg-purple-600 text-white rounded-md text-sm font-medium hover:bg-purple-700">
                Continue to Acceptance Certificate
              </button>
            )}
            {activeTab === "coa" && (
              <button onClick={onFinish} className="px-4 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700">
                Finish
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── New-quote / default view ──────────────────────────────────────────────
  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      <div className="border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-semibold text-gray-800">Create New Quote</h2>
          {activeTab !== "connection-type" && (
            <button onClick={resetForm}
              className="ml-4 inline-flex items-center px-3 py-1.5 rounded-lg bg-blue-50 text-blue-600 text-sm font-medium border border-blue-200 hover:bg-blue-100 hover:text-blue-700 transition duration-200">
              🔄 Start Over
            </button>
          )}
        </div>
      </div>

      <div className="p-6">
        <div className="mb-6">
          {activeTab === "connection-type" && (
            <div className="max-w-2xl mx-auto">
              <div className="text-center mb-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">How would you like to create this quote?</h3>
                <p className="text-gray-600">Choose to create a new customer connection or select from existing connections</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-1 gap-6">
                <div onClick={() => setConnectionType("existing")}
                  className={`p-6 border-2 rounded-xl cursor-pointer transition-all duration-200 ${connectionType === "existing" ? "border-blue-500 bg-blue-50" : "border-gray-200 hover:border-blue-300 hover:bg-gray-50"}`}>
                  <div className="text-center">
                    <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Users className="h-8 w-8 text-blue-600" />
                    </div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">Existing Quote</h4>
                    <p className="text-gray-600 text-sm">Select from your existing customer connections</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab !== "connection-type" && (
            <div className="flex flex-wrap gap-1 bg-gradient-to-r from-blue-800 via-blue-900 to-black rounded-t-lg p-1">
              {connectionType === "new" && (
                <TabBtn id="customer" activeTab={activeTab} setActiveTab={setActiveTab} label="CUSTOMER DETAILS" />
              )}
              <TabBtn id="system" activeTab={activeTab} setActiveTab={(id) => savedConnectionId && setActiveTab(id)} disabled={!savedConnectionId} label="SYSTEM CONFIG" />
              <TabBtn id="quote" activeTab={activeTab} setActiveTab={(id) => systemData && setActiveTab(id)} disabled={!systemData} icon={FileText} label="QUOTE" />
              <TabBtn id="contract" activeTab={activeTab} setActiveTab={(id) => systemData && setActiveTab(id)} disabled={!systemData} icon={FileSignature} label="CONTRACT" />
              <TabBtn id="rental" activeTab={activeTab} setActiveTab={(id) => systemData && setActiveTab(id)} disabled={!systemData} icon={Package} label="RENTAL" />
              <TabBtn id="gocardless" activeTab={activeTab} setActiveTab={(id) => systemData && setActiveTab(id)} disabled={!systemData} icon={CreditCard} label="GOCARDLESS" />
              <TabBtn id="assignment" activeTab={activeTab} setActiveTab={(id) => systemData && setActiveTab(id)} disabled={!systemData} icon={ClipboardList} label="ASSIGNMENT LETTER" />
              <TabBtn id="rental-agreement" activeTab={activeTab} setActiveTab={(id) => systemData && setActiveTab(id)} disabled={!systemData} icon={FileText} label="RENTAL AGREEMENT" />
              <TabBtn id="coa" activeTab={activeTab} setActiveTab={(id) => systemData && setActiveTab(id)} disabled={!systemData} icon={Award} label="ACCEPTANCE LETTER" />
            </div>
          )}

          <div>
            {activeTab === "customer" && connectionType === "new" && (
              <CustomerDetails ref={customerDetailsRef} onConnectionSaved={handleConnectionSaved} initialData={connectionData} />
            )}
            {activeTab === "system" && (
              <div className="mt-4">
                {savedConnectionId ? (
                  <>
                    {connectionData && (
                      <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-semibold text-blue-900">Selected Connection</h4>
                            <p className="text-blue-700">{connectionData.company_name} — {connectionData.md_name}</p>
                            <p className="text-sm text-blue-600">{connectionData.director_email}</p>
                          </div>
                        </div>
                      </div>
                    )}
                    <SystemConfig ref={systemConfigRef} selectedConnectionId={savedConnectionId} onSaveSuccess={handleSystemConfigSaved} />
                  </>
                ) : (
                  <div className="text-center py-8 text-gray-500">Loading connection details...</div>
                )}
              </div>
            )}
            {activeTab === "quote" && (
              <div className="mt-4">
                <TelecomQuotation systemData={systemData} selectedConnectionId={savedConnectionId} connectionData={connectionData} onSave={onFinish} onBack={handleBackFromQuote} onNext={() => setActiveTab("contract")} />
              </div>
            )}
            {activeTab === "contract" && (
              <div className="mt-4">
                <FibreContractForm systemData={systemData} selectedConnectionId={savedConnectionId} connectionData={connectionData} onSave={onFinish} onBack={handleBackFromContract} />
              </div>
            )}
            {activeTab === "rental" && (
              <div className="mt-4">
                <RentalDocumentForm systemData={systemData} selectedConnectionId={savedConnectionId} connectionData={connectionData} onSave={onFinish} onBack={handleBackFromRental} />
              </div>
            )}
            {activeTab === "gocardless" && (
              <GoCardlessTab connectionData={connectionData} selectedConnectionId={savedConnectionId} />
            )}
            {activeTab === "assignment" && (
              <AssignmentLetterTab connectionData={connectionData} selectedConnectionId={savedConnectionId} />
            )}
            {activeTab === "rental-agreement" && (
              <RentalAgreementTab connectionData={connectionData} selectedConnectionId={savedConnectionId} currentUser={currentUser} />
            )}
            {activeTab === "coa" && (
              <CoaTab connectionData={connectionData} selectedConnectionId={savedConnectionId} />
            )}
          </div>
        </div>

        {/* Navigation Buttons */}
        <div className="flex mt-6">
          {activeTab !== "connection-type" && (
            <button onClick={prevTab} className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50">
              Previous
            </button>
          )}
          {activeTab === "connection-type" && (
            <button onClick={nextTab} disabled={!connectionType}
              className={`ml-auto px-4 py-2 rounded-md text-sm font-medium text-white ${connectionType ? "bg-blue-600 hover:bg-blue-700" : "bg-gray-400 cursor-not-allowed"}`}>
              Continue
            </button>
          )}
          {activeTab === "quote" && (
            <button onClick={() => setActiveTab("contract")} className="ml-auto px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700">
              Continue to Contract
            </button>
          )}
          {activeTab === "contract" && (
            <button onClick={() => setActiveTab("rental")} className="ml-auto px-4 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700">
              Continue to Rental
            </button>
          )}
          {activeTab === "rental" && (
            <button onClick={() => setActiveTab("gocardless")} className="ml-auto px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700">
              Continue to GoCardless
            </button>
          )}
          {activeTab === "gocardless" && (
            <button onClick={() => setActiveTab("assignment")} className="ml-auto px-4 py-2 bg-purple-600 text-white rounded-md text-sm font-medium hover:bg-purple-700">
              Continue to Assignment Letter
            </button>
          )}
          {activeTab === "assignment" && (
            <button onClick={() => setActiveTab("rental-agreement")} className="ml-auto px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700">
              Continue to Rental Agreement
            </button>
          )}
          {activeTab === "rental-agreement" && (
             <button onClick={() => setActiveTab("coa")} className="ml-auto px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700">
              Continue to Acceptance Certificate
            </button>
          )}
          {activeTab === "coa" && (
            <button onClick={onFinish} className="ml-auto px-4 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700">
              Finish
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default NewQuoteForm;